import { useState, useEffect, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import RotationTestingPanel from "./RotationTestingPanel";
import { interpolateMergeTags } from "./mergeTags";
import { useModalA11y } from "./useModalA11y";
import RichTextEditor from "./richtext/RichTextEditor";
import WordRibbon from "./richtext/WordRibbon";
import { useVoiceDraft } from "./richtext/voiceDictation";
import { fetchTemplateRepo, markTemplateUsed } from "./emailTemplates";
import { SEQUENCE_THEME } from "./workspaceTheme";

// Phase 4a (Workspace/Mail Merge/Sequences unification, see
// linear-brewing-toast.md) — SequenceBuilder used to define its own local
// Fluent/Azure-blue palette here (`const T = {...}`, accent:"#0078D4"),
// a genuinely different palette from the rest of the Workspace/Mail Merge
// cluster's own Salesforce-Lightning `WORKSPACE_THEME`. `SEQUENCE_THEME`
// (workspaceTheme.js) was already built with an exact same-named
// counterpart for every key this local `T` had, specifically so this file
// could adopt it as a pure token-name swap — same convention
// OutlookWorkspace.js/MailMerge.js already use (`const C = WORKSPACE_THEME`).
const C = SEQUENCE_THEME;

const MERGE_TAGS = [
  { tag:"{{FirstName}}",    label:"First Name" },
  { tag:"{{LastName}}",     label:"Last Name" },
  { tag:"{{Title}}",        label:"Title" },
  { tag:"{{Company}}",      label:"Company" },
  { tag:"{{Email}}",        label:"Email" },
  { tag:"{{SenderName}}",   label:"Sender Name" },
  { tag:"{{SenderCompany}}",label:"Sender Company" },
];

const DEFAULT_STEPS = [
  { id:1, label:"Initial Email",  delayDays:0,  subject:"", body:"" },
  { id:2, label:"Follow-up 1",    delayDays:2,  subject:"", body:"" },
  { id:3, label:"Follow-up 2",    delayDays:4,  subject:"", body:"" },
  { id:4, label:"Follow-up 3",    delayDays:6,  subject:"", body:"" },
  { id:5, label:"Follow-up 4",    delayDays:7,  subject:"", body:"" },
  { id:6, label:"Follow-up 5",    delayDays:7,  subject:"", body:"" },
  { id:7, label:"Follow-up 6",    delayDays:7,  subject:"", body:"" },
];

const STEP_COLORS = [C.accent, C.cyan, C.amber, C.purple, C.green, C.red, C.accent];

// Restyled onto SEQUENCE_THEME (`C`) — was a hardcoded dark-navy border
// ("#1A1E2E") with a blue-purple default color ("#4F6EF7"), the same stray
// dark-theme leftover already fixed in CampaignsPage.js/ContactsPage.js
// (Phase 3) and App.js's Toast (Phase 1).
function Spinner({size=13,color=C.accent}){
  return <div style={{width:size,height:size,border:`2px solid ${C.border}`,borderTopColor:color,borderRadius:"50%",display:"inline-block",flexShrink:0,animation:"spin .7s linear infinite"}}/>;
}

function Badge({children,bg,tc,sm}){
  return <span style={{background:bg,color:tc,fontSize:sm?10:11,padding:sm?"2px 7px":"3px 9px",borderRadius:99,fontWeight:500,whiteSpace:"nowrap"}}>{children}</span>;
}

// Single, shared "insert merge tag" control. One button rather than a
// separate one per field — but to avoid reintroducing the original
// "which field does this go to?" confusion, the label itself always names
// its live target ("Insert tag  Subject line" / "  Email body"),
// tracking whichever field you last clicked into. Picking a tag inserts
// at the cursor in that field via applyMergeTag.
function TagPicker({open,onToggle,onPick,label="Insert tag"}){
  return(
    <div className="tag-picker" style={{position:"relative"}}>
      <button type="button" onMouseDown={e=>e.preventDefault()} onClick={onToggle}
        style={{display:"flex",alignItems:"center",gap:5,background:open?C.accentMuted:"transparent",color:C.accent,border:"1px solid "+(open?C.accent:C.border),borderRadius:6,padding:"3px 9px",fontSize:11,fontWeight:500,cursor:"pointer",whiteSpace:"nowrap"}}>
        <span style={{fontSize:12,lineHeight:1}}>{"{ }"}</span> {label}
      </button>
      {open&&(
        <div style={{position:"absolute",top:"calc(100% + 4px)",right:0,zIndex:20,background:C.bgCard,border:"1px solid "+C.border,borderRadius:8,boxShadow:"0 6px 20px rgba(16,24,40,.12)",padding:4,minWidth:190}}>
          {MERGE_TAGS.map(t=>(
            <button key={t.tag} type="button" onMouseDown={e=>e.preventDefault()} onClick={()=>onPick(t.tag)}
              style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,width:"100%",textAlign:"left",background:"none",border:"none",padding:"6px 8px",fontSize:12,color:C.text,borderRadius:5,cursor:"pointer"}}
              onMouseEnter={e=>e.currentTarget.style.background=C.bgHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <span>{t.label}</span>
              <span style={{fontSize:10,color:C.textMuted,fontFamily:"monospace"}}>{t.tag}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SequenceBuilder({settings={},addToast=()=>{}}){
  const [sequences,setSequences]=useState([]);
  const [contactFiles,setContactFiles]=useState([]);
  const [view,setView]=useState("list");
  const [editingSeq,setEditingSeq]=useState(null);
  const [seqCtxMenu,setSeqCtxMenu]=useState(null); // {x,y,seq} — right-click menu for the list-view sequence cards
  const seqCtxMenuRef=useRef(null);
  useModalA11y(!!seqCtxMenu,()=>setSeqCtxMenu(null),seqCtxMenuRef);
  const [activeStep,setActiveStep]=useState(0);
  // Shared template repository (server.js's /api/templates, see
  // emailTemplates.js) — same library Mail Merge, Workspace, and the main
  // app's Templates page all read/write. Fetched once; a step's "Insert
  // Template" picker filters it down to whichever category matches that
  // step (step 0 = fresh/first-touch, any later step = follow-up).
  const [templates,setTemplates]=useState([]);
  const [showTemplatePicker,setShowTemplatePicker]=useState(false);
  useEffect(()=>{ fetchTemplateRepo().then(setTemplates); },[]);
  const [showRotationStep,setShowRotationStep]=useState({}); // {[stepIndex]: bool} — collapsed by default per step
  const [showSendTimeRotation,setShowSendTimeRotation]=useState(false); // sequence-wide, not per-step — one flag, not a map
  const [saving,setSaving]=useState(false);
  // Word-style version history: every PUT save snapshots the pre-edit
  // content server-side (see server.js's snapshotSequenceVersion), so this
  // is purely a read+revert UI, no local diffing/state needed beyond the
  // fetched list itself.
  const [showVersions,setShowVersions]=useState(false);
  const [versionsList,setVersionsList]=useState([]);
  const [versionsLoading,setVersionsLoading]=useState(false);
  const [revertingId,setRevertingId]=useState(null);
  const versionsModalRef=useRef(null);
  useModalA11y(showVersions,()=>setShowVersions(false),versionsModalRef);

  // Rules (IF/THEN/ELSE conditional content) — ported from MailMerge.js's
  // "Rules" feature. Inserts a `[[IF:field=value]]...[[ELSE]]...[[ENDIF]]`
  // block into the step body; resolved at send time by the SAME shared
  // mergeTags.applyConditionalBlocks used for Mail Merge and already wired
  // into the sequence engine's body render (see applySequenceMergeTags in
  // server.js) — so this is a UI-only port, no backend changes needed.
  // Only "equals" is offered here (unlike MailMerge's dialog, which shows
  // an operator dropdown with contains/starts with/ends with options that
  // the underlying engine doesn't actually implement — those are silently
  // ignored there today). Not fixing that pre-existing MailMerge mismatch
  // in this pass, just not repeating it in a second place.
  const [showRules,setShowRules]=useState(false);
  const [rule,setRule]=useState({field:"Company",value:"",ifContent:"",elseContent:""});
  const rulesModalRef=useRef(null);
  useModalA11y(showRules,()=>setShowRules(false),rulesModalRef);
  const insertRuleTemplate=()=>{
    const ruleText=`\n\n[[IF:${rule.field}=${rule.value}]]\n${rule.ifContent||"[Content for matching recipients]"}\n[[ELSE]]\n${rule.elseContent||"[Content for other recipients]"}\n[[ENDIF]]\n`;
    const quill=quillRef.current?.getEditor?.();
    if(quill){
      const idx=quill.getLength();
      quill.insertText(idx,ruleText,"user");
    }else{
      const cur=editingSeq.steps[activeStep].body||"";
      updateStep(activeStep,"body",cur+ruleText);
    }
    setShowRules(false);
    addToast("Conditional rule inserted","success");
  };
  const [generating,setGenerating]=useState({});
  const [previewContact,setPreviewContact]=useState(null);
  const [contacts,setContacts]=useState([]);
  const [senders,setSenders]=useState([]);
  const [showMergeTags,setShowMergeTags]=useState(false);
  const [showStepMenu,setShowStepMenu]=useState(false);

  // Close an open tag picker on outside click / Escape, so it behaves like
  // any other dropdown instead of staying open until you happen to hit
  // Insert tag again.
  useEffect(()=>{
    if(!showMergeTags)return;
    const onDocClick=(e)=>{ if(!e.target.closest(".tag-picker"))setShowMergeTags(false); };
    const onEsc=(e)=>{ if(e.key==="Escape")setShowMergeTags(false); };
    document.addEventListener("mousedown",onDocClick);
    document.addEventListener("keydown",onEsc);
    return ()=>{ document.removeEventListener("mousedown",onDocClick); document.removeEventListener("keydown",onEsc); };
  },[showMergeTags]);

  // Same close-on-outside-click/Escape behavior for the step picker dropdown
  // that replaced the tall always-visible step sidebar.
  useEffect(()=>{
    if(!showStepMenu)return;
    const onDocClick=(e)=>{ if(!e.target.closest(".step-menu"))setShowStepMenu(false); };
    const onEsc=(e)=>{ if(e.key==="Escape")setShowStepMenu(false); };
    document.addEventListener("mousedown",onDocClick);
    document.addEventListener("keydown",onEsc);
    return ()=>{ document.removeEventListener("mousedown",onDocClick); document.removeEventListener("keydown",onEsc); };
  },[showStepMenu]);

  useEffect(()=>{
    fetchSequences();
    fetchContactFiles();
    fetch("/api/leads?limit=10000",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}}).then(r=>r.json()).then(setContacts).catch(()=>{});
    fetch("/api/smtp/accounts",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}}).then(r=>r.json()).then(d=>setSenders(Array.isArray(d)?d:[])).catch(()=>{});
  },[]);

  const fetchSequences=async()=>{
    try{const r=await fetch("/api/sequences",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});const d=await r.json();setSequences(Array.isArray(d)?d:[]);}catch(e){}
  };

  const fetchContactFiles=async()=>{
    try{const r=await fetch("/api/contact-files",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});const d=await r.json();setContactFiles(Array.isArray(d)?d:[]);}catch(e){}
  };

  const newSequence=()=>{
    if(sequences.length>=5){addToast("Maximum 5 sequences allowed. Delete one to create new.","error");return;}
    setEditingSeq({
      id:null,
      name:"",
      description:"",
      steps:DEFAULT_STEPS.map(s=>({...s})),
      contactFileId:null,
      contactFileName:"",
      totalContacts:0,
      status:"Draft",
    });
    setActiveStep(0);
    setContactMode("file");setSelectedContactIds([]);setContactSearch("");
    setView("edit");
  };

  const editSequence=(seq)=>{
    setEditingSeq({...seq,steps:seq.steps.map(s=>({...s}))});
    setActiveStep(0);
    setContactMode("file");setSelectedContactIds([]);setContactSearch("");
    setView("edit");
  };

  const saveSequence=async()=>{
    if(!editingSeq.name.trim()){addToast("Please enter a sequence name","error");return;}
    setSaving(true);
    try{
      const method=editingSeq.id?"PUT":"POST";
      const url=editingSeq.id?"/api/sequences/"+editingSeq.id:"/api/sequences";
      const r=await fetch(url,{method,headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify(editingSeq)});
      const d=await r.json();
      if(editingSeq.id){
        setSequences(p=>p.map(s=>s.id===d.id?d:s));
      }else{
        setSequences(p=>[...p,d]);
      }
      addToast("Sequence saved","success");
      setView("list");
    }catch(e){addToast("Save failed: "+e.message,"error");}
    setSaving(false);
  };

  const loadVersions=async()=>{
    if(!editingSeq?.id)return;
    setVersionsLoading(true);
    try{
      const r=await fetch("/api/sequences/"+editingSeq.id+"/versions",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      const d=await r.json();
      setVersionsList(Array.isArray(d)?d:[]);
    }catch(e){addToast("Couldn't load version history: "+e.message,"error");}
    setVersionsLoading(false);
  };
  const openVersionHistory=()=>{setShowVersions(true);loadVersions();};
  const revertToVersion=async(version)=>{
    if(!editingSeq?.id)return;
    if(!window.confirm(`Revert to the version saved ${new Date(version.createdAt).toLocaleString()}? Your current content will be saved as a new version first, so you can always undo this.`))return;
    setRevertingId(version.id);
    try{
      const r=await fetch(`/api/sequences/${editingSeq.id}/versions/${version.id}/revert`,{method:"POST",headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      setEditingSeq({...d,steps:(d.steps||[]).map(s=>({...s}))});
      setSequences(p=>p.map(s=>s.id===d.id?d:s));
      addToast("Reverted to the "+new Date(version.createdAt).toLocaleString()+" version","success");
      setShowVersions(false);
    }catch(e){addToast("Revert failed: "+e.message,"error");}
    setRevertingId(null);
  };

  const deleteSequence=async(id)=>{
    try{
      await fetch("/api/sequences/"+id,{method:"DELETE",headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      setSequences(p=>p.filter(s=>s.id!==id));
      addToast("Sequence deleted","info");
    }catch(e){addToast(e.message,"error");}
  };

  const duplicateSequence=async(id)=>{
    try{
      const r=await fetch("/api/sequences/"+id+"/duplicate",{method:"POST",headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      const d=await r.json();
      setSequences(p=>[...p,d]);
      addToast("Sequence duplicated","success");
    }catch(e){addToast(e.message,"error");}
  };
  // Shared start/pause/resume call — used by the list view's Start/Pause
  // button and by the Send Sequence controls inside the editor, so both
  // places report the same enrollment counts and can't drift apart.
  const runSequenceAction=async(seqId,action)=>{
    try{
      const r=await fetch("/api/sequences/"+seqId+"/"+action,{method:"POST",headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      if(action==="start"){
        addToast(`Sequence sending — ${d.enrolled} enrolled${d.skippedSuppressed?`, ${d.skippedSuppressed} skipped (already replied/bounced)`:""}${d.skippedExisting?`, ${d.skippedExisting} already in progress`:""}`,"success");
      }else if(action==="resume"){
        addToast("Sequence resumed","success");
      }else{
        addToast("Sequence paused — no more steps will send until resumed","info");
      }
      fetchSequences();
      return true;
    }catch(e){addToast("Couldn't "+action+": "+e.message,"error");return false;}
  };

  const toggleSequenceRun=(seq)=>{
    const action=seq.status==="Active"?"pause":seq.status==="Paused"?"resume":"start";
    return runSequenceAction(seq.id,action);
  };

  // Strip HTML so a Quill body that's technically non-empty (e.g. "<p><br></p>")
  // still counts as blank when deciding whether a step is really filled in.
  const plainTextLen=(html)=>(html||"").replace(/<[^>]*>/g,"").trim().length;

  // What's still missing before this sequence can actually send. Surfaced
  // both as a disabled-button tooltip and in the toast if someone forces it.
  const sequenceReadiness=()=>{
    const missing=[];
    if(!editingSeq?.name?.trim())missing.push("a sequence name");
    if(!editingSeq?.fromEmail)missing.push("a From email (sender)");
    if(!(editingSeq?.recipients||[]).length)missing.push("at least one recipient");
    const first=editingSeq?.steps?.[0];
    if(!first?.subject?.trim())missing.push("a subject on the Initial Email step");
    if(!plainTextLen(first?.body))missing.push("body content on the Initial Email step");
    return missing;
  };

  // Save (so a brand-new sequence has a real id) then immediately enroll
  // recipients and start sending — the "Send Sequence" button's full flow.
  const launchSequence=async()=>{
    const missing=sequenceReadiness();
    if(missing.length){addToast("Before sending, add: "+missing.join(", "),"error");return;}
    setSaving(true);
    try{
      const method=editingSeq.id?"PUT":"POST";
      const url=editingSeq.id?"/api/sequences/"+editingSeq.id:"/api/sequences";
      const r=await fetch(url,{method,headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify(editingSeq)});
      const saved=await r.json();
      if(!r.ok)throw new Error(saved.error||"Save failed");
      const ok=await runSequenceAction(saved.id,"start");
      if(ok)setView("list");
    }catch(e){addToast("Couldn't send: "+e.message,"error");}
    setSaving(false);
  };

  const updateStep=(idx,field,val)=>{
    setEditingSeq(p=>({
      ...p,
      steps:p.steps.map((s,i)=>i===idx?{...s,[field]:val}:s)
    }));
  };

  const lastFocusedRef=useRef("subject");
  const subjectRef=useRef(null);
  const quillRef=useRef(null);
  const [lastFocusedLabel,setLastFocusedLabel]=useState("subject");
  const [formatMarksOn,setFormatMarksOn]=useState(false);
  const getQuill=()=>quillRef.current?.getEditor?.();

  const setLastFocused=(val)=>{
    lastFocusedRef.current=val;
    setLastFocusedLabel(val);
  };

  const applyMergeTag=(tag)=>{
    if(lastFocusedRef.current==="subject"){
      const input=subjectRef.current;
      if(input){
        const start=input.selectionStart||0;
        const end=input.selectionEnd||0;
        const cur=editingSeq.steps[activeStep].subject||"";
        const newVal=cur.slice(0,start)+tag+cur.slice(end);
        updateStep(activeStep,"subject",newVal);
        setTimeout(()=>{
          input.focus();
          input.setSelectionRange(start+tag.length,start+tag.length);
        },10);
      }else{
        const cur=editingSeq.steps[activeStep].subject||"";
        updateStep(activeStep,"subject",cur+tag);
      }
    }else if(lastFocusedRef.current==="body"){
      const quill=quillRef.current?.getEditor?.();
      if(quill){
        const range=quill.getSelection(true);
        const index=range?range.index:quill.getLength();
        quill.insertText(index,tag,"user");
        quill.setSelection(index+tag.length,0);
      }else{
        const cur=editingSeq.steps[activeStep].body||"";
        updateStep(activeStep,"body",cur+tag);
      }
    }
  };

  const selectContactFile=async(fileId)=>{
    if(!fileId){
      setEditingSeq(p=>({...p,contactFileId:null,contactFileName:"",totalContacts:0,recipients:[],validEmailCount:0}));
      setPreviewContact(null);
      return;
    }
    const file=contactFiles.find(f=>f.id===fileId);
    if(!file)return;
    try{
      const res=await fetch("/api/leads?limit=10000",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      const allContacts=await res.json();
      const fileContacts=allContacts.filter(c=>c.contactFile===file.name);
      const suppressedCount=fileContacts.filter(c=>c.suppressed).length;
      const validRecipients=validateAndMapContacts(fileContacts);
      setEditingSeq(p=>({
        ...p,
        contactFileId:file.id,
        contactFileName:file.name,
        totalContacts:file.count,
        recipients:validRecipients,
        validEmailCount:validRecipients.length,
      }));
      if(validRecipients.length>0)setPreviewContact(validRecipients[0]);
      addToast("Auto-populated "+validRecipients.length+" recipients from "+file.name+(suppressedCount?` (${suppressedCount} excluded — already replied, bounced, or complained)`:""),"success");
    }catch(e){
      addToast("Error loading contacts: "+e.message,"error");
    }
  };

  const [contactMode,setContactMode]=useState("file"); // "file" | "individual"
  const [selectedContactIds,setSelectedContactIds]=useState([]);
  const [contactSearch,setContactSearch]=useState("");

  const validateAndMapContacts=(rawContacts)=>{
    const seen=new Set();
    return rawContacts.filter(c=>{
      if(c.suppressed)return false; // already responded, bounced, or complained — never re-contact
      const email=(c.email||"").trim().toLowerCase();
      if(!email||!email.includes("@")||!email.includes(".")){return false;}
      if(seen.has(email)){return false;}
      seen.add(email);
      return true;
    }).map(c=>({
      id:c.id, name:c.name||c.email, first:c.first||"", last:c.last||"",
      email:(c.email||"").trim().toLowerCase(), title:c.title||"", company:c.company||"", linkedin:c.linkedin||"",
    }));
  };

  const applyIndividualSelection=(ids)=>{
    const selected=contacts.filter(c=>ids.includes(c.id));
    const validRecipients=validateAndMapContacts(selected);
    setEditingSeq(p=>({
      ...p,
      contactFileId:null, contactFileName:ids.length?`${ids.length} contact${ids.length===1?"":"s"} selected`:"",
      totalContacts:ids.length,
      recipients:validRecipients,
      validEmailCount:validRecipients.length,
    }));
    if(validRecipients.length>0)setPreviewContact(validRecipients[0]);
  };
  const toggleContactSelection=(id)=>{
    setSelectedContactIds(p=>{
      const next=p.includes(id)?p.filter(x=>x!==id):[...p,id];
      applyIndividualSelection(next);
      return next;
    });
  };

  const [quickImporting,setQuickImporting]=useState(false);
  const quickImportFileRef=useRef(null);

  const autoMapHeaders=(hdrs)=>{
    const m={};
    const patterns={
      firstName:["first name","firstname","first_name","given name","fname","first"],
      lastName:["last name","lastname","last_name","surname","lname","last"],
      email:["email","email address","e-mail","mail","work email"],
      title:["title","job title","position","role","jobtitle"],
      company:["company","company name","organization","organisation","employer","account name"],
      linkedin:["linkedin","linkedin url","linkedin_url","profile url","person linkedin"],
    };
    hdrs.forEach(h=>{
      const hl=h.toLowerCase().trim();
      Object.entries(patterns).forEach(([field,pats])=>{
        if(!m[field]&&pats.some(p=>hl.includes(p)))m[field]=h;
      });
    });
    return m;
  };

  const quickImportFile=async(file)=>{
    if(!file)return;
    setQuickImporting(true);
    try{
      const ext=file.name.split(".").pop().toLowerCase();
      let rawData=[],hdrs=[];
      if(ext==="csv"){
        const parsed=await new Promise((resolve,reject)=>{
          Papa.parse(file,{header:true,skipEmptyLines:true,complete:resolve,error:reject});
        });
        hdrs=parsed.meta.fields||[];rawData=parsed.data;
      }else if(["xlsx","xls"].includes(ext)){
        const buf=await file.arrayBuffer();
        const wb=XLSX.read(buf,{type:"array"});
        const ws=wb.Sheets[wb.SheetNames[0]];
        rawData=XLSX.utils.sheet_to_json(ws,{defval:""});
        hdrs=rawData.length>0?Object.keys(rawData[0]):[];
      }else{
        addToast("Please upload a CSV or Excel file","error");setQuickImporting(false);return;
      }
      const mapping=autoMapHeaders(hdrs);
      if(!mapping.email){addToast("Couldn't find an email column in this file — check the header names","error");setQuickImporting(false);return;}
      const token=localStorage.getItem("sdr_token");
      const res=await fetch("/api/contacts/import",{
        method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body:JSON.stringify({contacts:rawData,mapping,fileName:file.name})
      });
      const data=await res.json();
      if(!res.ok)throw new Error(data.error||"Import failed");
      addToast(`Imported ${data.imported} contacts from ${file.name}`,"success");
      const contactsRes=await fetch("/api/leads?limit=10000",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      const freshContacts=await contactsRes.json();
      setContacts(Array.isArray(freshContacts)?freshContacts:[]);
      // Auto-select the newly imported contacts so they're immediately ready to use
      if(Array.isArray(data.contactIds)&&data.contactIds.length){
        setContactMode("individual");
        setSelectedContactIds(data.contactIds);
        applyIndividualSelection(data.contactIds);
      }
    }catch(e){addToast("Import failed: "+e.message,"error");}
    setQuickImporting(false);
  };

  const applyPreview=(text)=>{
    if(!previewContact)return text;
    return interpolateMergeTags(text,previewContact,{sdrName:settings.sdrName||"Andrew",company:settings.company||"SDR ReachOut"});
  };

  const aiGenerateStep=async(stepIdx)=>{
    const lead=previewContact||contacts[0];
    if(!lead){addToast("Select a contact file first to use AI generation","error");return;}
    setGenerating(p=>({...p,[stepIdx]:true}));
    const step=editingSeq.steps[stepIdx];
    const isFollowUp=stepIdx>0;
    const prevEmails=editingSeq.steps.slice(0,stepIdx).map(s=>(s.body||"").replace(/<[^>]+>/g,"").slice(0,150)).join("\n\n");
    try{
      const r=await fetch("/api/sequences/ai-generate-step",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          lead,settings,
          stepLabel:step.label,
          stepDay:"Day "+(editingSeq.steps.slice(0,stepIdx).reduce((a,s)=>a+s.delayDays,1)),
          stepDesc:step.label,
          prevEmails,isFollowUp
        })
      });
      const d=await r.json();
      if(d.subject)updateStep(stepIdx,"subject",d.subject.replace(lead.first||"","{{FirstName}}").replace(lead.company||"","{{Company}}"));
      if(d.body)updateStep(stepIdx,"body",d.body.replace(lead.first||"","{{FirstName}}").replace(lead.company||"","{{Company}}").replace(settings.sdrName||"","{{SenderName}}"));
      addToast(step.label+" generated","success");
    }catch(e){addToast("AI error: "+e.message,"error");}
    setGenerating(p=>({...p,[stepIdx]:false}));
  };

  const aiGenerateAll=async()=>{
    for(let i=0;i<editingSeq.steps.length;i++){
      await aiGenerateStep(i);
      await new Promise(r=>setTimeout(r,400));
    }
    addToast("All 7 steps generated","success");
  };

  const step=editingSeq?.steps?.[activeStep];
  const stepColor=STEP_COLORS[activeStep]||C.accent;
  const activeTotalDelay=editingSeq?.steps
    ? editingSeq.steps.slice(0,activeStep+1).reduce((a,st,idx)=>idx===0?1:a+st.delayDays,0)
    : 1;

  // Voice dictation (live continuous refinement) for whichever sequence step
  // is active — reads/writes editingSeq.steps[activeStep] via the same
  // updateStep() every other editor control in this step uses, so switching
  // steps mid-dictation keeps voice input scoped correctly.
  const voice=useVoiceDraft({
    getQuill,
    subject:step?.subject||"",
    setSubject:(v)=>updateStep(activeStep,"subject",v),
    setBodyHtml:(html)=>updateStep(activeStep,"body",html),
    sdrSettings:settings,
    addToast,
  });

  if(view==="list"){
    return(
      <div style={{fontFamily:"'Geist',sans-serif",animation:"fadeUp .25s ease both"}}>
        <style>{"@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}"}</style>

        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:22,flexWrap:"wrap",gap:10}}>
          <div>
            {/* Plain professional title — inherits the page's own 'Geist'
                sans-serif; no italic script face, no rainbow gradient fill. */}
            <p style={{fontSize:21,fontWeight:650,letterSpacing:"-.3px",lineHeight:1.25,color:C.text}}>Email Sequences</p>
            <p style={{fontSize:13,color:C.textSub,marginTop:3}}>{sequences.length} sequences  7 steps each  merge tags supported</p>
          </div>
          <button onClick={newSequence} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:500,cursor:"pointer"}}>
            + New Sequence
          </button>
        </div>

        {sequences.length===0&&(
          <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:"48px 20px",textAlign:"center"}}>
            <div style={{fontSize:40,marginBottom:10}}></div>
            <p style={{fontSize:14,color:C.textSub,marginBottom:4}}>No sequences yet</p>
            <p style={{fontSize:12,color:C.textMuted,marginBottom:16}}>Create a sequence with email templates and merge tags</p>
            <button onClick={newSequence} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 20px",fontSize:13,cursor:"pointer",fontWeight:500}}>
              Create first sequence
            </button>
          </div>
        )}

        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {sequences.map(seq=>(
            <div key={seq.id} onContextMenu={e=>{e.preventDefault();setSeqCtxMenu({x:e.clientX,y:e.clientY,seq});}}
              style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:"16px 20px"}}>
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12,marginBottom:12}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                    <p style={{fontSize:15,fontWeight:600,color:C.text}}>{seq.name}</p>
                    <Badge bg={seq.status==="Active"?C.greenMuted:C.amberMuted} tc={seq.status==="Active"?C.green:C.amber} sm>{seq.status}</Badge>
                    {seq.contactFileName&&<Badge bg={C.accentMuted} tc={C.accent} sm> {seq.contactFileName}</Badge>}
                  </div>
                  {seq.description&&<p style={{fontSize:12,color:C.textSub,marginBottom:6}}>{seq.description}</p>}
                  <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                    <span style={{fontSize:12,color:C.textSub}}> {seq.totalContacts||0} contacts</span>
                    <span style={{fontSize:12,color:C.textSub}}> {seq.steps?.length||7} steps</span>
                    <span style={{fontSize:12,color:C.textSub}}> Created {new Date(seq.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <div style={{display:"flex",gap:6,flexShrink:0,flexWrap:"wrap"}}>
                  <button onClick={()=>toggleSequenceRun(seq)} style={{background:seq.status==="Active"?C.amberMuted:C.greenMuted,color:seq.status==="Active"?C.amber:C.green,border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer",fontWeight:600}}>{seq.status==="Active"?"Pause":seq.status==="Paused"?"Resume":"Start"}</button>
                  <button onClick={()=>editSequence(seq)} style={{background:C.accentMuted,color:C.accent,border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>Edit</button>
                  <button onClick={()=>duplicateSequence(seq.id)} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>Duplicate</button>
                  <button onClick={()=>deleteSequence(seq.id)} style={{background:C.redMuted,color:C.red,border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>Delete</button>
                </div>
              </div>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                {(seq.steps||[]).map((s,i)=>(
                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:4}}>
                    <div style={{background:STEP_COLORS[i]+"22",border:"1px solid "+STEP_COLORS[i]+"44",borderRadius:6,padding:"3px 8px",fontSize:11,color:STEP_COLORS[i],whiteSpace:"nowrap"}}>
                      {s.label}
                      {s.subject&&<span style={{color:STEP_COLORS[i]+"99"}}> </span>}
                    </div>
                    {i<(seq.steps.length-1)&&<span style={{fontSize:10,color:C.textMuted}}></span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {seqCtxMenu&&(()=>{
          const {seq}=seqCtxMenu;
          const close=()=>setSeqCtxMenu(null);
          const items=[
            {label:seq.status==="Active"?"Pause":seq.status==="Paused"?"Resume":"Start",fn:()=>toggleSequenceRun(seq)},
            {label:"Edit",fn:()=>editSequence(seq)},
            {label:"Duplicate",fn:()=>duplicateSequence(seq.id)},
            {divider:true},
            {label:"Delete",fn:()=>deleteSequence(seq.id)},
          ];
          return(
            <div onClick={close} style={{position:"fixed",inset:0,zIndex:9998}}>
              <div ref={seqCtxMenuRef} tabIndex={-1} role="menu" onClick={e=>e.stopPropagation()} style={{position:"fixed",top:seqCtxMenu.y,left:seqCtxMenu.x,zIndex:9999,background:C.bgCard,border:"1px solid "+C.border,borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,.18)",minWidth:170,padding:"4px 0",fontSize:13,outline:"none"}}>
                {items.map((item,i)=>item.divider
                  ? <div key={i} style={{height:1,background:C.border,margin:"4px 0"}}/>
                  : (
                    <button key={i} onClick={()=>{item.fn();close();}}
                      style={{width:"100%",textAlign:"left",padding:"6px 14px",border:"none",background:"transparent",cursor:"pointer",color:C.text,fontSize:13}}
                      onMouseEnter={e=>e.currentTarget.style.background=C.bgHover}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      {item.label}
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  return(
    <div style={{fontFamily:"'Geist',sans-serif",animation:"fadeUp .25s ease both"}}>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}.ql-container{background:#ffffff!important;border:none!important;color:#201F1E;min-height:480px}.ql-editor{color:#201F1E!important;font-family:Calibri,'Segoe UI',sans-serif;font-size:14.5px;line-height:1.6;min-height:460px;max-height:min(72vh,900px);padding:28px 40px;resize:vertical;overflow-y:auto}.ql-editor p{margin:0}.ql-editor.ql-blank::before{color:#a19f9d;font-style:normal;left:40px}.ql-stroke{stroke:#605e5c!important}.ql-fill{fill:#605e5c!important}.ql-picker-label{color:#605e5c!important}.ql-picker-options{background:#ffffff!important;border-color:#d1cec9!important}.ql-picker-item{color:#201F1E!important}"}</style>

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button onClick={()=>setView("list")} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>
             Back
          </button>
          <div>
            <p style={{fontSize:16,fontWeight:600,color:C.text}}>{editingSeq?.id?"Edit":"New"} Sequence</p>
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",rowGap:8}}>
          <button onClick={aiGenerateAll} style={{background:C.accentMuted,color:C.accent,border:"none",borderRadius:7,padding:"6px 14px",fontSize:12,cursor:"pointer"}}>
            AI Generate All 7 Steps
          </button>
          <button onClick={saveSequence} disabled={saving} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"6px 16px",fontSize:12,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
            {saving?<><Spinner size={11}/> Saving...</>:"Save Draft"}
          </button>
          {editingSeq?.id&&(
            <button onClick={openVersionHistory} title="See earlier versions of this sequence and revert"
              style={{background:"none",color:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"6px 14px",fontSize:12,cursor:"pointer"}}>
              History
            </button>
          )}
          {editingSeq?.status==="Active"?(
            <button onClick={()=>runSequenceAction(editingSeq.id,"pause").then(ok=>{if(ok)setEditingSeq(p=>({...p,status:"Paused"}));})}
              style={{background:C.amberMuted,color:C.amber,border:"none",borderRadius:7,padding:"6px 16px",fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
               Pause Sending
            </button>
          ):editingSeq?.status==="Paused"?(
            <button onClick={()=>runSequenceAction(editingSeq.id,"resume").then(ok=>{if(ok)setEditingSeq(p=>({...p,status:"Active"}));})}
              style={{background:C.greenMuted,color:C.green,border:"none",borderRadius:7,padding:"6px 16px",fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
               Resume Sending
            </button>
          ):(
            <button onClick={launchSequence} disabled={saving||sequenceReadiness().length>0}
              title={sequenceReadiness().length?"Before sending, add: "+sequenceReadiness().join(", "):"Save and start sending this sequence now"}
              style={{background:sequenceReadiness().length?C.bgHover:C.green,color:sequenceReadiness().length?C.textMuted:"#fff",border:"none",borderRadius:7,padding:"6px 16px",fontSize:12,fontWeight:600,cursor:sequenceReadiness().length?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:5}}>
              {saving?<><Spinner size={11} color={sequenceReadiness().length?C.textMuted:"#fff"}/> Sending...</>:" Send Sequence"}
            </button>
          )}
        </div>
      </div>

      {/* Sequence name and settings */}
      <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:16,marginBottom:16}}>
        <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:5}}>Sequence Name *</p>
            <input
              value={editingSeq?.name||""}
              onChange={e=>setEditingSeq(p=>({...p,name:e.target.value}))}
              placeholder=""
              style={{width:"100%",background:C.bgHover,border:"1px solid "+C.border,borderRadius:7,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}
            />
          </div>
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
              <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",margin:0}}>Contacts</p>
              <div style={{display:"flex",gap:4}}>
                <button onClick={()=>setContactMode("file")} style={{fontSize:10,padding:"2px 8px",borderRadius:4,border:"1px solid "+C.border,background:contactMode==="file"?C.accent:"transparent",color:contactMode==="file"?"#fff":C.textSub,cursor:"pointer"}}>From File</button>
                <button onClick={()=>setContactMode("individual")} style={{fontSize:10,padding:"2px 8px",borderRadius:4,border:"1px solid "+C.border,background:contactMode==="individual"?C.accent:"transparent",color:contactMode==="individual"?"#fff":C.textSub,cursor:"pointer"}}>Select Contacts</button>
              </div>
            </div>
            {contactMode==="file"?(
              <select
                value={editingSeq?.contactFileId||""}
                onChange={e=>selectContactFile(e.target.value)}
                style={{width:"100%",background:C.bgHover,border:"1px solid "+C.border,borderRadius:7,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}
              >
                <option value="">-- Select contact file --</option>
                {contactFiles.map(f=>(
                  <option key={f.id} value={f.id}>{f.name} ({f.count} contacts)</option>
                ))}
              </select>
            ):(
              <div style={{border:"1px solid "+C.border,borderRadius:7,background:C.bgHover,overflow:"hidden"}}>
                <input value={contactSearch} onChange={e=>setContactSearch(e.target.value)} placeholder="Search contacts by name, email, or company..."
                  style={{width:"100%",border:"none",borderBottom:"1px solid "+C.border,padding:"8px 12px",fontSize:12,outline:"none",background:"transparent",color:C.text,boxSizing:"border-box"}}/>
                {(()=>{
                  const q=contactSearch.toLowerCase();
                  const matching=contacts.filter(c=>!q||(c.name||"").toLowerCase().includes(q)||(c.email||"").toLowerCase().includes(q)||(c.company||"").toLowerCase().includes(q));
                  const matchingIds=matching.filter(c=>!c.suppressed).map(c=>c.id);
                  const allMatchingSelected=matchingIds.length>0&&matchingIds.every(id=>selectedContactIds.includes(id));
                  return(
                    <>
                      {contacts.length>0&&(
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 12px",borderBottom:"1px solid "+C.border,background:"#fff"}}>
                          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:C.textSub,cursor:"pointer"}}>
                            <input type="checkbox" checked={allMatchingSelected} onChange={()=>{
                              const next=allMatchingSelected?selectedContactIds.filter(id=>!matchingIds.includes(id)):Array.from(new Set([...selectedContactIds,...matchingIds]));
                              setSelectedContactIds(next);applyIndividualSelection(next);
                            }} style={{width:"auto"}}/>
                            Select all {q?`matching (${matchingIds.length})`:`(${matchingIds.length})`}
                          </label>
                          {selectedContactIds.length>0&&(
                            <button onClick={()=>{setSelectedContactIds([]);applyIndividualSelection([]);}}
                              style={{fontSize:11,color:C.red,background:"none",border:"none",cursor:"pointer",padding:0}}>Clear all</button>
                          )}
                        </div>
                      )}
                      <div style={{maxHeight:180,overflowY:"auto"}}>
                        {contacts.length===0&&<p style={{padding:"10px 12px",fontSize:12,color:C.textSub}}>No contacts yet — add some on the Contacts page first.</p>}
                        {matching.length>200&&<p style={{padding:"6px 12px",fontSize:11,color:C.textSub,margin:0}}>Showing first 200 of {matching.length} — narrow your search to see more, or use Select All to include everyone matching.</p>}
                        {matching.slice(0,200).map(c=>(
                          <label key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 12px",cursor:c.suppressed?"default":"pointer",fontSize:12,borderBottom:"1px solid "+C.border+"55",opacity:c.suppressed?.5:1}}>
                            <input type="checkbox" disabled={!!c.suppressed} checked={selectedContactIds.includes(c.id)} onChange={()=>toggleContactSelection(c.id)} style={{width:"auto"}}/>
                            <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name||c.email} {c.company?<span style={{color:C.textSub}}>— {c.company}</span>:null}</span>
                            {c.suppressed&&<span style={{fontSize:10,color:C.red,flexShrink:0}} title={"Suppressed: "+c.suppressionReason}>{c.suppressionReason==="replied"?"Replied":c.suppressionReason==="complained"?"Complained":"Bounced"}</span>}
                          </label>
                        ))}
                      </div>
                    </>
                  );
                })()}
                {selectedContactIds.length>0&&<p style={{fontSize:11,color:C.green,padding:"6px 12px",margin:0,borderTop:"1px solid "+C.border}}>{selectedContactIds.length} selected</p>}
              </div>
            )}
          </div>
        </div>
        <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div>
            <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:5}}>Description</p>
            <input
              value={editingSeq?.description||""}
              onChange={e=>setEditingSeq(p=>({...p,description:e.target.value}))}
              placeholder=""
              style={{width:"100%",background:C.bgHover,border:"1px solid "+C.border,borderRadius:7,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}
            />
          </div>
          <div>
            <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:5}}>From Email (Sender)</p>
            <select
              value={editingSeq?.fromEmail||""}
              onChange={e=>{
                const sender=senders.find(s=>s.email===e.target.value);
                setEditingSeq(p=>({...p,fromEmail:e.target.value,fromName:sender?.name||""}));
              }}
              style={{width:"100%",background:C.bgHover,border:"1px solid "+C.border,borderRadius:7,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}
            >
              <option value="">-- Select sender email --</option>
              {senders.map(s=>(
                <option key={s.id} value={s.email}>{s.name}  {s.email}</option>
              ))}
            </select>
          </div>
          <div style={{display:"flex",alignItems:"flex-end",gap:10}}>
            {editingSeq?.contactFileName&&(
              <div style={{background:C.accentMuted,borderRadius:7,padding:"8px 12px",fontSize:12,color:C.accent,flex:1}}>
                 {editingSeq.contactFileName}  {editingSeq.totalContacts} total  <strong style={{color:C.green}}>{editingSeq.validEmailCount||0} valid emails auto-populated</strong>
              </div>
            )}
            {previewContact&&(
              <div style={{background:C.greenMuted,borderRadius:7,padding:"8px 12px",fontSize:12,color:C.green,flex:1}}>
                Preview: {previewContact.first} {previewContact.last}  {previewContact.email}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recipients panel */}
      {editingSeq?.recipients?.length>0&&(
        <div style={{background:C.bgCard,border:"1px solid "+C.green+"44",borderRadius:12,padding:16,marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div>
              <p style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:2}}>
                To Recipients  Auto-populated from {editingSeq.contactFileName}
              </p>
              <p style={{fontSize:12,color:C.textSub}}>
                {editingSeq.validEmailCount} valid emails  {editingSeq.totalContacts-editingSeq.validEmailCount} skipped (invalid/duplicate)
              </p>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>{
                const csv=editingSeq.recipients.map(r=>r.email).join("\n");
                navigator.clipboard.writeText(csv).then(()=>addToast("Emails copied","success"));
              }} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>
                Copy all emails
              </button>
            </div>
          </div>

          {/* Recipient stats */}
          <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:12}}>
            {[
              {label:"Total contacts",value:editingSeq.totalContacts||0,color:C.accent},
              {label:"Valid emails",value:editingSeq.validEmailCount||0,color:C.green},
              {label:"Skipped",value:(editingSeq.totalContacts||0)-(editingSeq.validEmailCount||0),color:C.amber},
              {label:"Ready to send",value:editingSeq.validEmailCount||0,color:C.green},
            ].map(s=>(
              <div key={s.label} style={{background:C.bgHover,borderRadius:8,padding:"8px 12px",textAlign:"center"}}>
                <p style={{fontSize:18,fontWeight:700,color:s.color,fontFamily:"monospace",lineHeight:1}}>{s.value}</p>
                <p style={{fontSize:10,color:C.textSub,marginTop:3}}>{s.label}</p>
              </div>
            ))}
          </div>

          {/* Recipient list */}
          <div style={{maxHeight:200,overflowY:"auto",border:"1px solid "+C.border,borderRadius:8,overflow:"hidden"}}>
            <div className="hide-on-mobile" style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1.5fr 40px",gap:8,padding:"7px 12px",background:C.bgHover,borderBottom:"1px solid "+C.border}}>
              {["Name","Company","Email",""].map(h=>(
                <span key={h} style={{fontSize:10,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",fontWeight:500}}>{h}</span>
              ))}
            </div>
            {editingSeq.recipients.map((r,i)=>(
              <div key={r.id} className="responsive-grid" style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1.5fr 40px",gap:8,padding:"7px 12px",borderBottom:i<editingSeq.recipients.length-1?"1px solid "+C.border:"none",alignItems:"center",background:i%2===0?C.bgCard:C.bgHover}}>
                <span style={{fontSize:12,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.name}</span>
                <span style={{fontSize:11,color:C.textSub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.company||""}</span>
                <span style={{fontSize:11,color:C.textSub,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.email}</span>
                <button onClick={()=>setEditingSeq(p=>({...p,recipients:p.recipients.filter((_,idx)=>idx!==i),validEmailCount:(p.validEmailCount||1)-1}))}
                  style={{background:C.redMuted,color:C.red,border:"none",borderRadius:4,padding:"2px 6px",fontSize:10,cursor:"pointer"}}>
                  
                </button>
              </div>
            ))}
          </div>

          {/* Add manual recipient */}
          <div style={{marginTop:10,display:"flex",gap:8}}>
            <input
              placeholder=""
              id="manual-email-input"
              style={{flex:1,background:C.bgHover,border:"1px solid "+C.border,borderRadius:7,padding:"7px 12px",color:C.text,fontSize:12,outline:"none"}}
              onKeyDown={e=>{
                if(e.key==="Enter"){
                  const val=e.target.value.trim().toLowerCase();
                  if(val&&val.includes("@")&&!editingSeq.recipients.find(r=>r.email===val)){
                    setEditingSeq(p=>({...p,recipients:[...p.recipients,{id:Date.now(),name:val,email:val,company:"",title:""}],validEmailCount:(p.validEmailCount||0)+1}));
                    e.target.value="";
                  }
                }
              }}
            />
            <button onClick={()=>{
              const input=document.getElementById("manual-email-input");
              const val=(input?.value||"").trim().toLowerCase();
              if(val&&val.includes("@")&&!editingSeq.recipients.find(r=>r.email===val)){
                setEditingSeq(p=>({...p,recipients:[...p.recipients,{id:Date.now(),name:val,email:val,company:"",title:""}],validEmailCount:(p.validEmailCount||0)+1}));
                if(input)input.value="";
              }
            }} style={{background:C.accent,color:"#fff",border:"none",borderRadius:7,padding:"7px 14px",fontSize:12,cursor:"pointer",fontWeight:500}}>
              + Add
            </button>
          </div>
        </div>
      )}

      <div>

        {/* Step editor */}
        {step&&(
          <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>

            {/* Step header — the old always-visible 7-card sidebar is now a
                compact dropdown here instead, so picking a step no longer
                eats a whole column of vertical page space. */}
            {/* Phase 4d (mobile-responsive pass, see linear-brewing-toast.md):
                this row (step picker + Insert tag/Rules/AI Draft/Voice) and
                its action-button cluster below were the one place in this
                view without flexWrap — every other multi-button row here
                already had it. On a phone-width viewport the five controls
                together are wider than the screen, so without wrap they'd
                overflow/clip instead of dropping to a second line. Restyle
                only — same buttons, same handlers, same layout at desktop
                widths (space-between with two children still reads as one
                row until it doesn't fit). */}
            <div style={{padding:"12px 16px",borderBottom:"1px solid "+C.border,display:"flex",alignItems:"center",justifyContent:"space-between",background:C.bgHover,position:"relative",flexWrap:"wrap",rowGap:8}}>
              <div className="step-menu" style={{position:"relative"}}>
                <button type="button" onMouseDown={e=>e.preventDefault()} onClick={()=>setShowStepMenu(v=>!v)}
                  style={{display:"flex",alignItems:"center",gap:9,background:showStepMenu?C.bgCard:"transparent",border:"1px solid "+(showStepMenu?C.border:"transparent"),borderRadius:7,padding:"5px 9px 5px 7px",cursor:"pointer"}}>
                  <div style={{width:10,height:10,borderRadius:"50%",background:stepColor,flexShrink:0}}/>
                  <span style={{fontSize:13,fontWeight:600,color:C.text}}>{step.label}</span>
                  <span style={{fontSize:11,color:C.textSub}}>Day {activeTotalDelay}</span>
                  <span style={{fontSize:9,color:C.textMuted,transform:showStepMenu?"rotate(180deg)":"none",transition:"transform .12s"}}></span>
                  {editingSeq.contactFileName&&<span style={{fontSize:12,color:C.textSub}}> {editingSeq.contactFileName}</span>}
                </button>
                {showStepMenu&&(
                  <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,zIndex:30,background:C.bgCard,border:"1px solid "+C.border,borderRadius:10,boxShadow:"0 10px 28px rgba(16,24,40,.16)",padding:6,minWidth:270,maxHeight:380,overflowY:"auto"}}>
                    {(editingSeq?.steps||[]).map((s,i)=>{
                      const isActive=activeStep===i;
                      const hasContent=!!(s.subject||s.body);
                      const color=STEP_COLORS[i]||C.accent;
                      const totalDelay=editingSeq.steps.slice(0,i+1).reduce((a,st,idx)=>idx===0?1:a+st.delayDays,0);
                      return(
                        <button key={s.id} type="button" onMouseDown={e=>e.preventDefault()} onClick={()=>{setActiveStep(i);setShowStepMenu(false);}}
                          style={{display:"flex",alignItems:"center",gap:10,width:"100%",textAlign:"left",background:isActive?C.accentMuted:"transparent",border:"1px solid "+(isActive?C.accent:"transparent"),borderRadius:7,padding:"7px 9px",cursor:"pointer",marginBottom:2}}
                          onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background=C.bgHover;}}
                          onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background="transparent";}}>
                          <div style={{width:20,height:20,borderRadius:"50%",background:hasContent?color+"22":C.bgHover,border:"2px solid "+(hasContent?color:C.border),display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:hasContent?color:C.textSub,flexShrink:0,fontFamily:"monospace"}}>
                            {hasContent?"":i+1}
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <p style={{fontSize:12,fontWeight:isActive?600:500,color:isActive?C.accent:C.text,lineHeight:1.3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.label}</p>
                            {s.subject&&<p style={{fontSize:10,color:C.textSub,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.subject.replace(/{{.*?}}/g,"").slice(0,40)}</p>}
                          </div>
                          <span style={{fontSize:10,color:C.textSub,flexShrink:0}}>Day {totalDelay}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",rowGap:8}}>
                <TagPicker
                  open={showMergeTags}
                  onToggle={()=>setShowMergeTags(v=>!v)}
                  onPick={t=>{applyMergeTag(t);setShowMergeTags(false);}}
                  label={"Insert tag  "+(lastFocusedLabel==="body"?"Email body":"Subject line")}
                />
                <button onClick={()=>setShowRules(true)} title="Insert conditional content based on a recipient field (IF/THEN/ELSE)"
                  style={{background:C.purpleMuted,color:C.purple,border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer",fontWeight:600}}>
                  Rules
                </button>
                <button onClick={()=>aiGenerateStep(activeStep)} disabled={!!generating[activeStep]}
                  style={{background:C.accentMuted,color:C.accent,border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
                  {generating[activeStep]?<><Spinner size={11}/>Generating...</>:"AI Draft This Step"}
                </button>
                <button onClick={voice.toggleListening} disabled={!voice.supported}
                  title={!voice.supported?"Voice dictation needs Chrome or Edge":(voice.listening?"Stop voice dictation":"Dictate this step by voice")}
                  style={{background:voice.listening?"#d13438":C.accentMuted,color:voice.listening?"#fff":C.accent,border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:voice.supported?"pointer":"not-allowed",display:"flex",alignItems:"center",gap:5,opacity:voice.supported?1:0.5}}>
                  {voice.listening?<><span style={{width:7,height:7,borderRadius:"50%",background:"#fff",display:"inline-block",animation:"spin 1s linear infinite"}}/> {voice.busy?"Updating…":"Listening"}</>:<>&#127908; Voice</>}
                </button>
              </div>
            </div>
            {voice.listening&&voice.interimText&&(
              <div style={{padding:"6px 16px",borderBottom:"1px solid "+C.border,background:"#FFF9E6",fontSize:12,color:C.textSub,fontStyle:"italic"}}>
                {voice.interimText}
              </div>
            )}

            {/* Delay config */}
            <div style={{padding:"10px 16px",borderBottom:"1px solid "+C.border,display:"flex",alignItems:"center",gap:12,background:C.bgHover,flexWrap:"wrap",rowGap:6}}>
              <span style={{fontSize:12,color:C.textSub}}>Send after</span>
              <input
                type="number"
                min={0}
                max={30}
                value={step.delayDays}
                onChange={e=>updateStep(activeStep,"delayDays",parseInt(e.target.value)||0)}
                style={{width:60,background:C.bgCard,border:"1px solid "+C.border,borderRadius:6,padding:"4px 8px",color:C.text,fontSize:13,outline:"none",textAlign:"center"}}
                disabled={activeStep===0}
              />
              <span style={{fontSize:12,color:C.textSub}}>days{activeStep===0?" (initial email  sent on Day 1)":""}</span>
              {previewContact&&(
                <span style={{fontSize:12,color:C.green,marginLeft:"auto"}}>
                  Preview: {previewContact.first} at {previewContact.company}
                </span>
              )}
            </div>

            {/* Insert Template — pulls from the shared repository (Mail
                Merge/Workspace/Templates page all read the same one),
                pre-filtered to whichever category matches this step. */}
            <div style={{padding:"8px 16px 0",position:"relative"}}>
              <button type="button" onClick={()=>setShowTemplatePicker(v=>!v)}
                style={{fontSize:11,fontWeight:600,color:C.accent,background:C.accentMuted,border:"1px solid "+C.border,borderRadius:6,padding:"5px 10px",cursor:"pointer"}}>
                 Insert Template{showTemplatePicker?" ▴":" ▾"}
              </button>
              {showTemplatePicker&&(()=>{
                const wantCategory=activeStep===0?"fresh":"followup";
                const matching=templates.filter(t=>(t.category||"general")===wantCategory);
                const rest=templates.filter(t=>(t.category||"general")!==wantCategory);
                const applyTemplateStep=(t)=>{
                  updateStep(activeStep,"subject",t.subject||"");
                  updateStep(activeStep,"body",t.body||"<p><br></p>");
                  markTemplateUsed(t.id);
                  setShowTemplatePicker(false);
                };
                return(
                  <div style={{position:"absolute",top:"calc(100% + 4px)",left:16,right:16,zIndex:50,background:C.bgCard,border:"1px solid "+C.border,borderRadius:8,boxShadow:"0 8px 24px rgba(16,24,40,.18)",maxHeight:280,overflowY:"auto"}}>
                    {templates.length===0&&(
                      <p style={{fontSize:12,color:C.textMuted,padding:"14px 12px"}}>No templates yet — save one from Mail Merge, Workspace, or the Templates page.</p>
                    )}
                    {matching.length>0&&(
                      <p style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:".4px",padding:"8px 12px 2px"}}>
                        {activeStep===0?"Fresh Email":"Follow-up"} templates
                      </p>
                    )}
                    {matching.map(t=>(
                      <div key={t.id} onClick={()=>applyTemplateStep(t)} style={{padding:"8px 12px",cursor:"pointer",borderBottom:"1px solid "+C.border}}
                        onMouseEnter={e=>e.currentTarget.style.background=C.bgHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <p style={{fontSize:12,fontWeight:600,color:C.text}}>{t.name}</p>
                        <p style={{fontSize:11,color:C.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.subject}</p>
                      </div>
                    ))}
                    {rest.length>0&&(
                      <p style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:".4px",padding:"8px 12px 2px"}}>Other templates</p>
                    )}
                    {rest.map(t=>(
                      <div key={t.id} onClick={()=>applyTemplateStep(t)} style={{padding:"8px 12px",cursor:"pointer",borderBottom:"1px solid "+C.border}}
                        onMouseEnter={e=>e.currentTarget.style.background=C.bgHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <p style={{fontSize:12,fontWeight:600,color:C.text}}>{t.name}</p>
                        <p style={{fontSize:11,color:C.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.subject}</p>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Subject */}
            <div style={{padding:"10px 16px",borderBottom:"1px solid "+C.border}}>
              <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:6}}>Subject line</p>
              <input
                ref={subjectRef}
                value={step.subject||""}
                onChange={e=>updateStep(activeStep,"subject",e.target.value)}
                onFocus={()=>setLastFocused("subject")}
                onClick={()=>setLastFocused("subject")}
                placeholder=""
                style={{width:"100%",background:C.bgHover,border:"1px solid "+(lastFocusedLabel==="subject"?C.accent:C.border),borderRadius:7,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}
              />
              {previewContact&&step.subject&&(
                <p style={{fontSize:11,color:C.green,marginTop:4}}>Preview: {applyPreview(step.subject)}</p>
              )}
            </div>

            {/* Rotation & Testing — opt-in per step, per dimension (subject
                line, opening line, from name, signature/CTA). When a
                dimension is enabled, the corresponding field above/in the
                template is used only as the fallback (rotation disabled or
                no active variants); the engine picks from the rotation pool
                on every send instead. Only shown for a saved sequence, since
                rotation config is keyed by the sequence's real id. */}
            {editingSeq.id&&(
              <div style={{padding:"0 16px 10px"}}>
                <button type="button" onClick={()=>setShowRotationStep(p=>({...p,[activeStep]:!p[activeStep]}))}
                  style={{fontSize:11,color:C.purple,background:"none",border:"none",cursor:"pointer",padding:"4px 0",fontWeight:600}}>
                  {showRotationStep[activeStep]?"▾":"▸"} Rotation & testing for this step
                </button>
                {showRotationStep[activeStep]&&(
                  <RotationTestingPanel ownerType="sequence" ownerId={editingSeq.id} stepIndex={activeStep} theme={C}/>
                )}
              </div>
            )}

            {/* Send Time rotation — deliberately NOT per-step like the panel
                above. It's one shared time-window policy for the whole
                sequence (see RotationTestingPanel.js/server.js for why), so
                it's rendered once here regardless of which step tab is
                open, rather than once per step. Also applies from the 2nd
                email onward only — the first touch always sends as soon as
                the sequence's general send window allows. */}
            {editingSeq.id&&activeStep===0&&(
              <div style={{padding:"0 16px 10px"}}>
                <button type="button" onClick={()=>setShowSendTimeRotation(p=>!p)}
                  style={{fontSize:11,color:C.purple,background:"none",border:"none",cursor:"pointer",padding:"4px 0",fontWeight:600}}>
                  {showSendTimeRotation?"▾":"▸"} Send time rotation for this sequence
                </button>
                {showSendTimeRotation&&(
                  <RotationTestingPanel ownerType="sequence" ownerId={editingSeq.id} stepIndex={null} includeSendTime theme={C}/>
                )}
              </div>
            )}

            {/* Body */}
            <div style={{padding:"10px 16px 0"}}>
              <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:6}}>Email body</p>
              <div style={{border:"1px solid "+C.border,borderRadius:8,overflow:"hidden"}}>
                <div style={{background:"#F8F7F6",borderBottom:"1px solid "+C.border,padding:"2px 4px",overflowX:"auto"}}>
                  <WordRibbon getQuill={getQuill} showStyles formatMarksOn={formatMarksOn} onToggleFormatMarks={()=>setFormatMarksOn(v=>!v)}/>
                </div>
                <div onClickCapture={()=>setLastFocused("body")} onMouseDownCapture={()=>setLastFocused("body")}>
                  <RichTextEditor
                    ref={quillRef}
                    // Pre-existing bug fixed here (reproduces identically
                    // against the un-migrated code, confirmed via git stash):
                    // a literal "" doesn't match Quill's own canonical
                    // empty-editor HTML ("<p><br></p>"), so react-quill's
                    // controlled-value diffing (shouldComponentUpdate ->
                    // setEditorContents) treats switching to an untouched
                    // step as a real content change, which synchronously
                    // re-fires onChange with the *still-stale* onChange
                    // closure from the step you're navigating away from —
                    // silently overwriting that step's just-typed body with
                    // empty content. Matching Quill's own empty-HTML string
                    // here removes the mismatch that triggers it.
                    value={step.body||"<p><br></p>"}
                    onChange={val=>updateStep(activeStep,"body",val)}
                    formatMarksOn={formatMarksOn}
                    placeholder=""
                  />
                </div>
              </div>
              {previewContact&&step.body&&(
                <div style={{marginTop:8,padding:"10px 12px",background:C.bgHover,borderRadius:7,fontSize:12,color:C.textSub,lineHeight:1.6}}>
                  <p style={{fontSize:10,color:C.green,textTransform:"uppercase",letterSpacing:".5px",marginBottom:4}}>Preview with contact data</p>
                  <div dangerouslySetInnerHTML={{__html:applyPreview(step.body)}}/>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div style={{padding:"12px 16px",borderTop:"1px solid "+C.border,display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:12,flexWrap:"wrap",rowGap:8}}>
              <button onClick={()=>setActiveStep(Math.max(0,activeStep-1))} disabled={activeStep===0}
                style={{background:C.bgHover,color:activeStep===0?C.textMuted:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"5px 12px",fontSize:12,cursor:activeStep===0?"not-allowed":"pointer"}}>
                 Previous
              </button>
              <span style={{fontSize:12,color:C.textSub}}>
                Step {activeStep+1} of {editingSeq.steps.length}  {editingSeq.steps.filter(s=>s.subject).length} completed
              </span>
              <button onClick={()=>setActiveStep(Math.min((editingSeq.steps.length-1),activeStep+1))} disabled={activeStep===editingSeq.steps.length-1}
                style={{background:C.bgHover,color:activeStep===editingSeq.steps.length-1?C.textMuted:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"5px 12px",fontSize:12,cursor:activeStep===editingSeq.steps.length-1?"not-allowed":"pointer"}}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Version history — Word-style "see what this looked like earlier,
          one-click revert". Read+revert only; every save already snapshots
          server-side, so there's no local diffing to do here. */}
      {showVersions&&(
        <div onClick={()=>setShowVersions(false)} style={{position:"fixed",inset:0,zIndex:9998,background:"rgba(0,0,0,.35)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div ref={versionsModalRef} tabIndex={-1} role="dialog" aria-label="Version History" onClick={e=>e.stopPropagation()}
            style={{background:C.bgCard,borderRadius:12,padding:20,width:520,maxWidth:"90vw",maxHeight:"80vh",display:"flex",flexDirection:"column",fontFamily:"'Geist',sans-serif",fontSize:13,boxShadow:"0 8px 32px rgba(0,0,0,.25)",outline:"none"}}>
            <h3 style={{margin:"0 0 4px",fontSize:15,color:C.text}}>Version History</h3>
            <p style={{margin:"0 0 12px",fontSize:12,color:C.textSub,lineHeight:1.4}}>Every save keeps the version it replaced. Revert to bring an older version back as the current one — reverting saves your current content as a version too, so nothing is ever lost.</p>
            <div style={{flex:1,overflowY:"auto",border:"1px solid "+C.border,borderRadius:8}}>
              {versionsLoading?(
                <div style={{padding:20,textAlign:"center",color:C.textSub,fontSize:12}}>Loading…</div>
              ):versionsList.length===0?(
                <div style={{padding:20,textAlign:"center",color:C.textSub,fontSize:12}}>No earlier versions yet — this sequence has only been saved once.</div>
              ):versionsList.map((v,i)=>(
                <div key={v.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderBottom:i<versionsList.length-1?"1px solid "+C.border:"none"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{new Date(v.createdAt).toLocaleString()}</div>
                    <div style={{fontSize:11,color:C.textSub}}>
                      {v.reason==="revert"?"Before a revert":"Before an edit"} · {v.name} · {v.stepCount} step{v.stepCount===1?"":"s"}
                    </div>
                  </div>
                  <button onClick={()=>revertToVersion(v)} disabled={revertingId===v.id}
                    style={{background:C.accentMuted,color:C.accent,border:"none",borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:revertingId===v.id?"default":"pointer",flexShrink:0}}>
                    {revertingId===v.id?"Reverting…":"Revert"}
                  </button>
                </div>
              ))}
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:14}}>
              <button onClick={()=>setShowVersions(false)} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"6px 16px",fontSize:13,cursor:"pointer"}}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Rules — Insert Conditional Content (IF/THEN/ELSE), ported from
          MailMerge.js. Inserts a [[IF:field=value]]...[[ELSE]]...[[ENDIF]]
          block at the end of the step body; already resolved at send time
          by the shared mergeTags module, so no backend changes needed. */}
      {showRules&&(
        <div onClick={()=>setShowRules(false)} style={{position:"fixed",inset:0,zIndex:9998,background:"rgba(0,0,0,.35)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div ref={rulesModalRef} tabIndex={-1} role="dialog" aria-label="Insert Conditional Rule" onClick={e=>e.stopPropagation()}
            style={{background:C.bgCard,borderRadius:12,padding:20,width:500,maxWidth:"90vw",fontFamily:"'Geist',sans-serif",fontSize:13,boxShadow:"0 8px 32px rgba(0,0,0,.25)",outline:"none"}}>
            <h3 style={{margin:"0 0 4px",fontSize:15,color:C.text}}>Insert Conditional Rule (IF/THEN/ELSE)</h3>
            <p style={{margin:"0 0 14px",fontSize:12,color:C.textSub,lineHeight:1.4}}>Create conditional content based on a recipient data field. Inserted into the email body for this step.</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
              <div>
                <p style={{fontSize:11,fontWeight:600,color:C.text,marginBottom:4}}>Field</p>
                <select value={rule.field} onChange={e=>setRule(p=>({...p,field:e.target.value}))}
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"6px 8px",fontSize:12,color:C.text,outline:"none"}}>
                  {["Company","Title","Industry","Location","Email"].map(o=><option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <p style={{fontSize:11,fontWeight:600,color:C.text,marginBottom:4}}>Equals</p>
                <input value={rule.value} onChange={e=>setRule(p=>({...p,value:e.target.value}))}
                  placeholder="e.g. Healthcare"
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"6px 8px",fontSize:12,color:C.text,outline:"none",boxSizing:"border-box"}}/>
              </div>
            </div>
            <div style={{marginBottom:10}}>
              <p style={{fontSize:11,fontWeight:600,color:C.green,marginBottom:4}}>IF MATCH — show this content:</p>
              <textarea value={rule.ifContent} onChange={e=>setRule(p=>({...p,ifContent:e.target.value}))}
                placeholder="Content shown when condition matches..."
                rows={3} style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"6px 8px",fontSize:12,resize:"vertical",fontFamily:"'Geist',sans-serif",outline:"none",boxSizing:"border-box"}}/>
            </div>
            <div style={{marginBottom:14}}>
              <p style={{fontSize:11,fontWeight:600,color:C.amber,marginBottom:4}}>ELSE — show this content:</p>
              <textarea value={rule.elseContent} onChange={e=>setRule(p=>({...p,elseContent:e.target.value}))}
                placeholder="Content shown when condition does not match..."
                rows={3} style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"6px 8px",fontSize:12,resize:"vertical",fontFamily:"'Geist',sans-serif",outline:"none",boxSizing:"border-box"}}/>
            </div>
            <div style={{background:C.bgHover,border:"1px solid "+C.border,borderRadius:6,padding:"8px 12px",marginBottom:14,fontSize:11,color:C.textSub,fontFamily:"monospace"}}>
              [[IF:{rule.field||"Company"}={rule.value||"value"}]]<br/>
              {rule.ifContent||"[IF content]"}<br/>
              [[ELSE]]<br/>
              {rule.elseContent||"[ELSE content]"}<br/>
              [[ENDIF]]
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setShowRules(false)} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"6px 16px",fontSize:13,cursor:"pointer"}}>Cancel</button>
              <button onClick={insertRuleTemplate} disabled={!rule.value.trim()} style={{background:rule.value.trim()?C.accent:C.border,color:rule.value.trim()?"#fff":C.textMuted,border:"none",borderRadius:7,padding:"6px 16px",fontSize:13,cursor:rule.value.trim()?"pointer":"not-allowed",fontWeight:600}}>Insert Rule</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
